import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { snapshotsApi, Snapshot } from '@/lib/snapshots';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';

/**
 * Public snapshot view — anyone with the link can read it. The payload
 * shape is whatever the creator passed; we render the common
 * "conversation transcript with citations" shape if present, otherwise
 * fall back to a raw JSON pretty-print.
 */

interface ConversationPayload {
  kind: 'conversation';
  title?: string;
  turns: { role: 'user' | 'assistant'; content: string; citedChunkIds?: string[]; model?: string }[];
}

interface AskAnswerPayload {
  kind: 'ask_answer';
  answer: string;
  citedChunkIds?: (string | number)[];
  model?: string | null;
  costCents?: number;
  inputTokens?: number;
  outputTokens?: number;
}

export function SharedSnapshotPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery<Snapshot>({
    queryKey: ['snapshot', id],
    queryFn: () => snapshotsApi.getPublic(id!),
    enabled: !!id,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 py-10 space-y-6">
        {isLoading && <div className="text-ink-500 text-sm">{t('common.loading', { defaultValue: 'Loading…' })}</div>}
        {error && (
          <div className="rounded-2xl border border-ink-200 bg-white p-12 text-center">
            <h1 className="text-2xl font-semibold text-ink-900">
              {t('common.not_found_title', { defaultValue: 'Not found' })}
            </h1>
            <p className="mt-1 text-sm text-ink-600">
              {t('common.not_found_sub', { defaultValue: 'This shared snapshot has expired or never existed.' })}
            </p>
          </div>
        )}
        {data && <SnapshotView snap={data} />}
      </main>
      <SiteFooter />
    </div>
  );
}

function SnapshotView({ snap }: { snap: Snapshot }) {
  const payload = snap.payload as ConversationPayload | AskAnswerPayload | Record<string, unknown>;
  const kind = (payload as { kind?: string })?.kind;
  const isConversation = kind === 'conversation';
  const isAskAnswer = kind === 'ask_answer';
  return (
    <>
      <div>
        <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-indigo-700 bg-indigo-50 rounded-md px-2 py-0.5 mb-2">
          Shared snapshot
        </div>
        <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{snap.label}</h1>
        <p className="mt-1 text-ink-600 num text-xs">
          {new Date(snap.createdAt).toLocaleString()}
          {snap.expiresAt && <> · expires {new Date(snap.expiresAt).toLocaleDateString()}</>}
        </p>
      </div>

      {isConversation ? (
        <div className="space-y-4">
          {(payload as ConversationPayload).turns.map((turn, i) =>
            turn.role === 'user' ? (
              <div
                key={i}
                className="rounded-xl bg-indigo-600 text-white px-3 py-2 text-sm max-w-2xl ml-auto whitespace-pre-wrap"
              >
                {turn.content}
              </div>
            ) : (
              <div key={i} className="rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm">
                <CitedMarkdown content={turn.content} />
                {turn.model && (
                  <div className="mt-2 text-[10px] font-mono text-ink-400">{turn.model}</div>
                )}
              </div>
            ),
          )}
        </div>
      ) : isAskAnswer ? (
        <AskAnswerView payload={payload as AskAnswerPayload} />
      ) : (
        <pre className="rounded-xl border border-ink-200 bg-white p-4 text-xs overflow-auto">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </>
  );
}

function AskAnswerView({ payload }: { payload: AskAnswerPayload }) {
  const { t } = useTranslation();
  const cited = payload.citedChunkIds ?? [];
  // Public page: deliberately NO model id / token counts / cost — those are
  // internal details that shouldn't leak to anyone holding the share link.
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-ink-200 bg-white px-5 py-4">
        <CitedMarkdown content={payload.answer ?? ''} />
      </div>

      {cited.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-ink-500">
            {t('snapshot.citations', { defaultValue: 'Citations' })}
          </span>
          {cited.map((id) => (
            <span
              key={String(id)}
              className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
            >
              #{String(id)}
            </span>
          ))}
        </div>
      )}

      <p className="text-[11px] text-ink-400">
        {t('snapshot.generated_by', { defaultValue: 'Generated with Knowlez AI' })}
      </p>
    </div>
  );
}
